import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, UserCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CONTACT_REOPEN_KEY = "vivo_open_contact";
const MAX = 500;
const REASONS = ["Sugerencia", "Problema", "Agradecer", "Dudas"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY = { name: "", email: "", reason: "", message: "", website: "" };

export function ContactModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [registered, setRegistered] = useState(false);
  const [busy, setBusy] = useState(false);
  const checked = useRef("");

  useEffect(() => {
    if (!open) return;
    setErrors({}); setRegistered(false); checked.current = "";
    setF(user ? { ...EMPTY, name: (user.name || "").slice(0, 100), email: user.email || "" } : EMPTY);
  }, [open, user]);

  useEffect(() => {
    if (user || !open) return;
    const email = f.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setRegistered(false); return; }
    const t = setTimeout(() => {
      if (checked.current === email) return;
      checked.current = email;
      api.post("/contact/check-email", { email }).then((r) => setRegistered(!!r.data.registered)).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [f.email, user, open]);

  const goLogin = () => {
    sessionStorage.setItem(CONTACT_REOPEN_KEY, "1");
    onOpenChange(false);
    navigate(`/login?email=${encodeURIComponent(f.email.trim())}`);
  };

  const submit = async () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Ingresa tu nombre completo";
    if (!EMAIL_RE.test(f.email.trim())) e.email = "Ingresa un correo electrónico válido";
    if (!f.reason) e.reason = "Selecciona el motivo del contacto";
    if (f.message.trim().length < 5) e.message = "Escribe tu mensaje";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      await api.post("/contact", { ...f, name: f.name.trim(), email: f.email.trim() });
      toast.success("¡Mensaje enviado con éxito! Nos pondremos en contacto contigo a la brevedad.");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const set = (k) => (v) => { setF((s) => ({ ...s, [k]: v })); if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined })); };
  const Err = ({ k }) => errors[k] ? <p className="text-xs text-destructive mt-1" data-testid={`contact-error-${k}`}>{errors[k]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="contact-modal">
        <DialogHeader>
          <DialogTitle>Contactar a VIVOTaller</DialogTitle>
          <DialogDescription>Escríbenos y te respondemos a tu correo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {user && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400" data-testid="contact-session-note">
              <UserCheck className="w-4 h-4" />Completamos tus datos con tu sesión iniciada.
            </p>
          )}
          <div>
            <Label>Nombre completo</Label>
            <Input value={f.name} onChange={(e) => set("name")(e.target.value.slice(0, 100))} maxLength={100} aria-invalid={!!errors.name} data-testid="contact-name" />
            <Err k="name" />
          </div>
          <div>
            <Label>Correo electrónico</Label>
            <Input type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} readOnly={!!user}
              className={user ? "bg-muted" : ""} aria-invalid={!!errors.email} data-testid="contact-email" />
            <Err k="email" />
            {!errors.email && !user && f.email.trim() && !EMAIL_RE.test(f.email.trim()) && (
              <p className="text-xs text-destructive mt-1" data-testid="contact-email-format">Ingresa un correo válido con la forma nombre@dominio.cl</p>
            )}
            {!user && registered && (
              <div className="mt-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs" data-testid="contact-registered-banner">
                Detectamos que tienes una cuenta registrada. ¿Deseas{" "}
                <button type="button" onClick={goLogin} className="font-semibold text-primary underline underline-offset-2" data-testid="contact-login-link">Iniciar Sesión</button>
                {" "}para completar tus datos automáticamente?
              </div>
            )}
          </div>
          <div>
            <Label>Motivo del contacto</Label>
            <Select value={f.reason} onValueChange={set("reason")}>
              <SelectTrigger aria-invalid={!!errors.reason} data-testid="contact-reason"><SelectValue placeholder="Selecciona un motivo" /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r} data-testid={`contact-reason-${r}`}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <Err k="reason" />
          </div>
          <div>
            <Label>Mensaje</Label>
            <Textarea rows={5} value={f.message} maxLength={MAX} aria-invalid={!!errors.message} data-testid="contact-message"
              onChange={(e) => set("message")(e.target.value.slice(0, MAX))} placeholder="Cuéntanos en qué te podemos ayudar…" />
            <div className="flex justify-between gap-2 mt-1">
              <Err k="message" />
              <span className={`ml-auto text-xs tabular-nums ${f.message.length >= MAX ? "text-destructive font-medium" : "text-muted-foreground"}`} data-testid="contact-counter">
                {f.message.length} / {MAX} caracteres
              </span>
            </div>
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden"
            value={f.website} onChange={(e) => set("website")(e.target.value)} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contact-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="contact-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Enviar Mensaje</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
